import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import styled, { FlattenInterpolation, ThemeProps, css } from 'styled-components';

const baseHeading = css`
  ${fonts.helvetica['93-black-extended']};
  text-transform: uppercase;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const H1Style = (important?: boolean): FlattenInterpolation<ThemeProps<any>> => css`
  ${baseHeading};
  font-size: 32px ${important ? '!important' : null};
  line-height: 32px ${important ? '!important' : null};

  @media ${from(Device.Tablet)} {
    font-size: 40px ${important ? '!important' : null};
    line-height: 42px ${important ? '!important' : null};
  }

  @media ${from(Device.Desktop)} {
    font-size: 50px ${important ? '!important' : null};
    line-height: 50px ${important ? '!important' : null};
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 60px ${important ? '!important' : null};
    line-height: 60px ${important ? '!important' : null};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const H2Style = (important?: boolean): FlattenInterpolation<ThemeProps<any>> => css`
  ${baseHeading};
  font-size: 24px ${important ? '!important' : null};
  line-height: 26px ${important ? '!important' : null};

  @media ${from(Device.Tablet)} {
    font-size: 32px ${important ? '!important' : null};
    line-height: 32px ${important ? '!important' : null};
  }

  @media ${from(Device.Desktop)} {
    font-size: 40px ${important ? '!important' : null};
    line-height: 40px ${important ? '!important' : null};
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 50px ${important ? '!important' : null};
    line-height: 56px ${important ? '!important' : null};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const H3Style = (important?: boolean): FlattenInterpolation<ThemeProps<any>> => css`
  ${baseHeading};
  font-size: 24px ${important ? '!important' : null};
  line-height: 26px ${important ? '!important' : null};

  @media ${from(Device.Tablet)} {
    font-size: 28px ${important ? '!important' : null};
    line-height: 30px ${important ? '!important' : null};
  }

  @media ${from(Device.Desktop)} {
    font-size: 30px ${important ? '!important' : null};
    line-height: 34px ${important ? '!important' : null};
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 40px ${important ? '!important' : null};
    line-height: 44px ${important ? '!important' : null};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const H4Style = (important?: boolean): FlattenInterpolation<ThemeProps<any>> => css`
  ${baseHeading}
  font-size: 21px ${important ? '!important' : null};
  line-height: 24px ${important ? '!important' : null};

  @media ${from(Device.Tablet)} {
    font-size: 24px ${important ? '!important' : null};
    line-height: 28px ${important ? '!important' : null};
  }

  @media ${from(Device.Desktop)} {
    font-size: 24px ${important ? '!important' : null};
    line-height: 28px ${important ? '!important' : null};
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 30px ${important ? '!important' : null};
    line-height: 36px ${important ? '!important' : null};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const H5Style = (important?: boolean): FlattenInterpolation<ThemeProps<any>> => css`
  ${baseHeading}
  font-size: 18px ${important ? '!important' : null};
  line-height: 21px ${important ? '!important' : null};

  @media ${from(Device.Tablet)} {
    font-size: 21px ${important ? '!important' : null};
    line-height: 24px ${important ? '!important' : null};
  }

  @media ${from(Device.Desktop)} {
    font-size: 21px ${important ? '!important' : null};
    line-height: 24px ${important ? '!important' : null};
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 24px ${important ? '!important' : null};
    line-height: 28px ${important ? '!important' : null};
  }
`;

const H6Style = css`
  ${baseHeading}
  font-size: 16px;
  line-height: 18px;

  @media ${from(Device.Tablet)} {
    font-size: 18px;
    line-height: 21px;
  }

  @media ${from(Device.Desktop)} {
    font-size: 18px;
    line-height: 24px;
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 21px;
    line-height: 24px;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PStyle = (important?: boolean): FlattenInterpolation<ThemeProps<any>> => css`
  ${fonts.helvetica['55-roman']};
  font-size: 16px ${important ? '!important' : null};
  line-height: 28px ${important ? '!important' : null};

  @media ${from(Device.Tablet)} {
    font-size: 16px ${important ? '!important' : null};
    line-height: 28px ${important ? '!important' : null};
  }

  @media ${from(Device.Desktop)} {
    font-size: 16px ${important ? '!important' : null};
    line-height: 28px ${important ? '!important' : null};
  }

  @media ${from(Device.DesktopLarge)} {
    font-size: 18px ${important ? '!important' : null};
    line-height: 30px ${important ? '!important' : null};
  }
`;

const PStyleSmall = css`
  ${fonts.helvetica['55-roman']};
  font-size: 16px;
  line-height: 28px;
`;

const PStyleSmallest = css`
  ${fonts.helvetica['55-roman']};
  font-size: 16px;
  line-height: 28px;
`;

const H1 = styled.h1`
  ${H1Style(false)};
`;

const H2 = styled.h2`
  ${H2Style(false)};
`;

const H3 = styled.h3`
  ${H3Style(false)};
`;

const H4 = styled.h4`
  ${H4Style(false)};
`;

const H5 = styled.h5`
  ${H5Style(false)};
`;

const H6 = styled.h6`
  ${H6Style};
`;

const P = styled.p`
  ${PStyle(false)};
`;

const PSmall = styled.p`
  ${PStyleSmall};
`;

const PSmallest = styled.p`
  ${PStyleSmallest};
`;

export const Typography = {
  H1Style: H1Style(false),
  H1StyleImportant: H1Style(true),
  H2Style: H2Style(false),
  H2StyleImportant: H2Style(true),
  H3Style: H3Style(false),
  H3StyleImportant: H3Style(true),
  H4Style: H4Style(false),
  H4StyleImportant: H4Style(true),
  H5Style: H5Style(false),
  H5StyleImportant: H5Style(true),
  H6Style,
  PStyle: PStyle(false),
  PStyleImportant: PStyle(true),
  PStyleSmall,
  PStyleSmallest,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  P,
  PSmall,
  PSmallest,
};
